/**
 * Popup and dropdown primitives: `<Popup>`, `<PopupPanel>`,
 * `<DropDownSelector>`.
 *
 * `<Popup>` is a thin wrapper around `platform-specific/popup` so callers never
 * import from `platform-specific/` directly. `<PopupPanel>` pairs a trigger
 * child with a chevron indicator. `<DropDownSelector>` is a labelled select
 * built on `<PopupPanel>` that renders the current selection and a flyout list
 * of options.
 */

import { ChevronDown, ChevronUp } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { getFontSize } from 'platform-specific/font';
import { PopupBase } from 'platform-specific/popup';
import { useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { TLabel } from 'util/stdtypes';

import { HorizBox, Pad } from './basics';
import { UtilityText } from './text';
import { useTranslate } from './translation';
import { TextButton } from './ui/text-button';

export type PopupPanelProps = {
    children: React.ReactNode;
    popupContent: (props: { onClose: () => void }) => React.JSX.Element;
    alignRight?: boolean;
    testID?: string;
    setHover?: (hover: boolean) => void;
};

export function PopupPanel({ children, popupContent, alignRight = false, testID, setHover }: PopupPanelProps) {
    const { colors } = useTheme();
    const s = PopupPanelStyle;
    const [shown, setShown] = useState(false);
    return (
        <View style={{ alignSelf: 'flex-start' }}>
            <PopupBase alignRight={alignRight} popupContent={popupContent} setHover={setHover} setShown={setShown} testID={testID}>
                <View style={s.button}>
                    {children}
                    <Pad size={8} />
                    {shown ? (
                        <ChevronUp color={colors.colorTextPrimary} size={16} />
                    ) : (
                        <ChevronDown color={colors.colorTextPrimary} size={16} />
                    )}
                </View>
            </PopupBase>
        </View>
    );
}

const PopupPanelStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        alignItems: 'center',
    },
});

export type PopupProps = {
    popupContent: (props: { onClose: () => void }) => React.JSX.Element;
    setShown?: (shown: boolean) => void;
    testID?: string;
    children: React.ReactNode;
    alignTop?: boolean;
    alignRight?: boolean;
    centerOnTrigger?: boolean;
    popupGap?: number;
    popupEnabled?: boolean;
    popupStyle?: StyleProp<ViewStyle>;
    onClose?: () => void;
    setHover?: (hover: boolean) => void;
    onPress?: () => void;
    matchWidth?: boolean;
};

// Need to have this here rather than in platform-specific, because it's hard
// for other components to import from platform-specific
export function Popup(params: PopupProps) {
    return <PopupBase {...params} />;
}

export type DropDownSelectorProps = {
    testID?: string;
    label?: TLabel;
    text?: string;
    options: Array<{ key: string; label?: TLabel; text?: string }>;
    value?: string;
    onChange?: (k: string) => void;
};

export function DropDownSelector({ testID, label, text, options, value, onChange = (k: string) => {} }: DropDownSelectorProps) {
    const { colors } = useTheme();
    const s = DropDownSelectorStyle;
    const translate = useTranslate();
    const [hover, setHover] = useState(false);
    const selectedOption = options.find((o) => o.key === value) || options[0];
    function popupContent({ onClose }: { onClose: () => void }) {
        return (
            <View style={{ backgroundColor: colors.colorSurfacePrimary }}>
                {options.map((o, i) => (
                    <View key={o.key}>
                        {i !== 0 && <Pad size={20} />}
                        <TextButton
                            onPress={() => {
                                onChange(o.key);
                                onClose();
                            }}
                            style={s.option}
                            testID={o.label?.defaultText || o.text}
                            text={o.label ? translate(o.label) : (o.text ?? o.key)}
                            variant='primary'
                        />
                    </View>
                ))}
            </View>
        );
    }
    return (
        <PopupPanel alignRight popupContent={popupContent} setHover={setHover} testID={testID}>
            <HorizBox>
                <UtilityText label={label} text={text} type='small' weight='medium' />
                {
                    (label || text) && (
                        <UtilityText text=': ' type='small' weight='medium' />
                    ) /** Do not render a lone colon if no label provided*/
                }
                <UtilityText label={selectedOption.label} text={selectedOption.text} type='small' underline={hover} weight='medium' />
            </HorizBox>
        </PopupPanel>
    );
}

const DropDownSelectorStyle = StyleSheet.create({
    option: {
        fontSize: getFontSize(14),
        textDecorationLine: 'none',
    },
});
